import { normalizeDateOnly } from '../lib/dates';
import { validateEmail } from '../lib/validators';
import {
  Appointment,
  CheckoutQuoteRequest,
  Company,
  OpayoPaymentRequest,
  SystemConfig,
  SystemConfigUpdatePayload,
  TemplateConfig
} from '../types';

const VALID_BOOKING_STATUSES = new Set(['booked', 'completed', 'cancelled', 'noshow']);
const VALID_QUESTIONNAIRE_STATUSES = new Set(['not_required', 'pending', 'submitted']);

const toTrimmedString = (value: unknown) => String(value ?? '').trim();

const toNullableString = (value: unknown): string | null => {
  const normalized = toTrimmedString(value);
  return normalized || null;
};

const toOptionalTrimmedString = (value: unknown): string | undefined => {
  if (value === undefined) return undefined;
  const normalized = toTrimmedString(value);
  return normalized || undefined;
};

const normalizeAllowedVenueIds = (value: unknown): string[] | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (!Array.isArray(value)) return undefined;

  const normalized = [...new Set(value.map((item) => toTrimmedString(item)).filter(Boolean))];
  return normalized;
};

const normalizeChangeLogsValue = (value: unknown) => {
  if (value === undefined || value === null) return undefined;

  let parsed: unknown = value;
  if (typeof parsed === 'string') {
    const normalized = parsed.trim();
    if (!normalized) return undefined;
    try {
      parsed = JSON.parse(normalized);
    } catch (_err) {
      return undefined;
    }
  }

  if (Array.isArray(parsed)) {
    return parsed.filter((item) => item && typeof item === 'object');
  }

  if (parsed && typeof parsed === 'object') {
    return parsed as Record<string, unknown>;
  }

  return undefined;
};

const isDateLike = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isNaN(new Date(value).getTime());

const isTimeLike = (value: string) => /^\d{2}:\d{2}(?::\d{2})?$/.test(value);

const normalizeTimeLike = (value: unknown) => {
  const normalized = toTrimmedString(value);
  const match = normalized.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return normalized;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = match[3];
  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return normalized;
  }

  const hh = String(hours).padStart(2, '0');
  return seconds ? `${hh}:${match[2]}:${seconds}` : `${hh}:${match[2]}`;
};

const normalizeIsoDateTime = (value: unknown) => {
  const normalized = toTrimmedString(value);
  if (!normalized) return new Date().toISOString();
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return new Date().toISOString();
  return parsed.toISOString();
};

const normalizeStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  return [...new Set(value.map((item) => toTrimmedString(item)).filter(Boolean))];
};

const areJsonValuesEqual = (left: unknown, right: unknown) =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

const buildChangedSectionPatch = <T extends object>(
  nextSection: T | undefined,
  currentSection: T | undefined
): Partial<T> | undefined => {
  if (!nextSection) return undefined;

  const currentSectionRecord = (currentSection || {}) as Record<string, unknown>;
  const patchEntries = Object.entries(nextSection as Record<string, unknown>).filter(
    ([key, value]) => !areJsonValuesEqual(value, currentSectionRecord[key])
  );

  if (patchEntries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(patchEntries) as Partial<T>;
};

const buildTemplateConfigPatch = (
  nextTemplates: TemplateConfig | undefined,
  currentTemplates: TemplateConfig | undefined
): SystemConfigUpdatePayload['templates'] | undefined => {
  if (!nextTemplates) return undefined;

  const patch: SystemConfigUpdatePayload['templates'] = {};

  if (!areJsonValuesEqual(nextTemplates.emailTemplates, currentTemplates?.emailTemplates)) {
    patch.emailTemplates = nextTemplates.emailTemplates;
  }
  if (!areJsonValuesEqual(nextTemplates.smsTemplates, currentTemplates?.smsTemplates)) {
    patch.smsTemplates = nextTemplates.smsTemplates;
  }
  if (!areJsonValuesEqual(nextTemplates.reminderSettings, currentTemplates?.reminderSettings)) {
    patch.reminderSettings = nextTemplates.reminderSettings;
  }
  if (
    !areJsonValuesEqual(nextTemplates.notificationSettings, currentTemplates?.notificationSettings)
  ) {
    patch.notificationSettings = nextTemplates.notificationSettings;
  }
  if (
    !areJsonValuesEqual(nextTemplates.certificateTemplate, currentTemplates?.certificateTemplate)
  ) {
    patch.certificateTemplate = nextTemplates.certificateTemplate;
  }

  return Object.keys(patch).length > 0 ? patch : undefined;
};

export const buildCreateBookingPayload = (booking: Appointment) => {
  const id = toTrimmedString(booking.id);
  const venueId = toTrimmedString(booking.venueId);
  const serviceIds = (
    Array.isArray(booking.serviceIds) && booking.serviceIds.length > 0
      ? booking.serviceIds
      : [booking.serviceId]
  )
    .map((value) => toTrimmedString(value))
    .filter((value): value is string => Boolean(value))
    .filter((value, index, list) => list.indexOf(value) === index);
  const serviceId = serviceIds[0] || '';
  const customerName = toTrimmedString(booking.customerName);
  const customerEmail = toTrimmedString(booking.customerEmail).toLowerCase();
  const normalizedDate = normalizeDateOnly(booking.date);
  const normalizedTime = normalizeTimeLike(booking.time);
  const normalizedCompanyId = toNullableString(booking.companyId);
  const addOnIds = Array.isArray(booking.addOnIds)
    ? booking.addOnIds
        .map((value) => toTrimmedString(value))
        .filter((value): value is string => Boolean(value))
    : [];
  const paymentMethod =
    booking.paymentMethod === 'card' || booking.paymentMethod === 'invoice'
      ? booking.paymentMethod
      : normalizedCompanyId
        ? 'invoice'
        : 'card';
  const normalizedPaymentId = paymentMethod === 'card' ? toNullableString(booking.paymentId) : null;
  const normalizedPaymentLinkToken =
    paymentMethod === 'card' ? toNullableString(booking.paymentLinkToken) : null;

  if (!venueId) throw new Error('Venue is required to create a booking.');
  if (!serviceId) throw new Error('Service is required to create a booking.');
  if (!customerName) throw new Error('Customer name is required to create a booking.');
  if (!customerEmail) throw new Error('Customer email is required to create a booking.');
  if (!validateEmail(customerEmail).valid) {
    throw new Error('Please enter a valid email address.');
  }
  if (!normalizedDate) throw new Error('Booking date is required.');
  if (!normalizedTime) throw new Error('Booking time is required.');
  if (!isDateLike(normalizedDate)) {
    throw new Error('Booking date is invalid. Use YYYY-MM-DD.');
  }
  if (!isTimeLike(normalizedTime)) {
    throw new Error('Booking time is invalid. Use HH:mm.');
  }

  return {
    ...(id ? { id } : {}),
    venueId,
    serviceId,
    serviceIds,
    addOnIds,
    date: normalizedDate,
    time: normalizedTime,
    customerName,
    customerEmail,
    customerPhone: toNullableString(booking.customerPhone),
    customerDob: toNullableString(booking.customerDob),
    customerJobTitle: toNullableString(booking.customerJobTitle),
    companyId: normalizedCompanyId,
    poNumber: toNullableString(booking.poNumber),
    paymentMethod,
    paymentId: normalizedPaymentId,
    paymentLinkToken: normalizedPaymentLinkToken,
    questionnaireStatus: VALID_QUESTIONNAIRE_STATUSES.has(booking.questionnaireStatus)
      ? booking.questionnaireStatus
      : 'not_required',
    allowOverbook: booking.allowOverbook === true ? true : undefined,
    status: VALID_BOOKING_STATUSES.has(booking.status) ? booking.status : 'booked',
    questionnaireSubmissionId: toNullableString(booking.questionnaireSubmissionId),
    certificateId: toNullableString(booking.certificateId),
    createdAt: normalizeIsoDateTime(booking.createdAt)
  };
};

export const buildUpdateCompanyPayload = (company: Partial<Company> & { id: string }) => {
  const payload: Record<string, unknown> = {};
  const source = company as Record<string, unknown>;

  const name = toOptionalTrimmedString(source.name);
  if (name) payload.name = name;

  const contactPerson = toOptionalTrimmedString(source.contactPerson);
  if (contactPerson) payload.contactPerson = contactPerson;

  const email = toOptionalTrimmedString(source.email);
  if (email) payload.email = email.toLowerCase();

  const address = toOptionalTrimmedString(source.address);
  if (address) payload.address = address;

  if (Object.prototype.hasOwnProperty.call(source, 'phone')) {
    payload.phone = toNullableString(source.phone);
  }

  const status = toOptionalTrimmedString(source.status);
  if (status) payload.status = status;

  const tier = toOptionalTrimmedString(source.tier);
  if (tier) payload.tier = tier;

  const paymentPreference = toOptionalTrimmedString(source.paymentPreference);
  if (paymentPreference) payload.paymentPreference = paymentPreference;

  if (Object.prototype.hasOwnProperty.call(source, 'requirePo')) {
    payload.requirePo = Boolean(source.requirePo);
  }

  const allowedVenueIds = normalizeAllowedVenueIds(source.allowedVenueIds);
  if (allowedVenueIds !== undefined) payload.allowedVenueIds = allowedVenueIds;

  if (Object.prototype.hasOwnProperty.call(source, 'staffNotes')) {
    payload.staffNotes = toNullableString(source.staffNotes);
  }

  const changeLogs = normalizeChangeLogsValue(source.changeLogs);
  if (changeLogs !== undefined) payload.changeLogs = changeLogs;

  if (Object.keys(payload).length === 0) {
    throw new Error('No valid company changes to save.');
  }

  return payload;
};

export const buildUpdateSystemConfigPayload = (
  nextConfig: SystemConfig,
  currentConfig?: SystemConfig | null
): SystemConfigUpdatePayload => {
  if (!currentConfig) {
    return {
      general: nextConfig.general,
      verification: nextConfig.verification,
      smtp: nextConfig.smtp,
      twilio: nextConfig.twilio,
      opayo: nextConfig.opayo,
      sage: nextConfig.sage,
      templates: nextConfig.templates,
      termsAndConditions: nextConfig.termsAndConditions
    };
  }

  const payload: SystemConfigUpdatePayload = {};

  const generalPatch = buildChangedSectionPatch(nextConfig.general, currentConfig.general);
  if (generalPatch) payload.general = generalPatch;

  const verificationPatch = buildChangedSectionPatch(
    nextConfig.verification,
    currentConfig.verification
  );
  if (verificationPatch) payload.verification = verificationPatch;

  const smtpPatch = buildChangedSectionPatch(nextConfig.smtp, currentConfig.smtp);
  if (smtpPatch) payload.smtp = smtpPatch;

  const twilioPatch = buildChangedSectionPatch(nextConfig.twilio, currentConfig.twilio);
  if (twilioPatch) payload.twilio = twilioPatch;

  const opayoPatch = buildChangedSectionPatch(nextConfig.opayo, currentConfig.opayo);
  if (opayoPatch) payload.opayo = opayoPatch;

  const sagePatch = buildChangedSectionPatch(nextConfig.sage, currentConfig.sage);
  if (sagePatch) payload.sage = sagePatch;

  const templatesPatch = buildTemplateConfigPatch(nextConfig.templates, currentConfig.templates);
  if (templatesPatch) payload.templates = templatesPatch;

  if (!areJsonValuesEqual(nextConfig.termsAndConditions, currentConfig.termsAndConditions)) {
    payload.termsAndConditions = nextConfig.termsAndConditions;
  }

  return payload;
};

export const buildCheckoutQuotePayload = (payload: CheckoutQuoteRequest): CheckoutQuoteRequest => {
  const serviceId = toTrimmedString(payload.serviceId);
  if (!serviceId) {
    throw new Error('Service is required to get checkout quote.');
  }

  const addOnIds = normalizeStringArray(payload.addOnIds);
  const serviceIds = normalizeStringArray(payload.serviceIds) ?? [];
  return {
    ...payload,
    serviceId,
    ...(serviceIds.length > 0 ? { serviceIds } : {}),
    addOnIds,
    companyId: toOptionalTrimmedString(payload.companyId),
    currency: toOptionalTrimmedString(payload.currency)
  };
};

export const buildOpayoPaymentPayload = (payload: OpayoPaymentRequest): OpayoPaymentRequest => {
  const serviceId = toTrimmedString(payload.serviceId);
  const customerEmail = toTrimmedString(payload.customer?.email).toLowerCase();
  if (!serviceId) {
    throw new Error('Service is required to process payment.');
  }
  if (!customerEmail || !validateEmail(customerEmail).valid) {
    throw new Error('Please enter a valid email address.');
  }

  const addOnIds = normalizeStringArray(payload.addOnIds);
  const serviceIds = normalizeStringArray(payload.serviceIds) ?? [];
  return {
    ...payload,
    customer: {
      ...payload.customer,
      email: customerEmail
    },
    serviceId,
    ...(serviceIds.length > 0 ? { serviceIds } : {}),
    addOnIds,
    companyId: toOptionalTrimmedString(payload.companyId),
    appointmentId: toOptionalTrimmedString(payload.appointmentId),
    currency: toOptionalTrimmedString(payload.currency),
    description: toOptionalTrimmedString(payload.description)
  };
};
